import Swiper from "swiper";
import {createSwiper} from "../../application/utils/swiper";
import {Navigation} from "swiper/modules";

export const BlockJobNetwork = ( block: HTMLElement ) => {
	const NetworkObject = {
		container: block,
		nodes: block.querySelectorAll( '.network-node' ) as NodeListOf<HTMLElement>,
		overlay: block.querySelector( '.network-overlay' ) as HTMLElement,
		slider: block.querySelector( '[data-swiper="network"]' ) as HTMLElement,
		closeButtons: [] as HTMLButtonElement[],
		activeCloseButton: null as HTMLButtonElement|null,
		activeNode: null as HTMLElement|null,
		swiper: null as unknown as Swiper,
		_init(): void {
			this._bindClass();
			this._initSwiper();
			this._createCloseButtons();
			this._enhanceAccessibility();
			this._bindEvents();
			this._observeVisibility();
		},
		_initSwiper(): void {
			this.swiper = createSwiper(
				this.container.querySelector('[data-swiper="network"]') as HTMLElement,
				'network-slide',
				{
					modules: [
						Navigation
					],
					centeredSlides: true,
					loop: false,
					navigation: {
						nextEl: '.network-slider__button--next',
						prevEl: '.network-slider__button--prev',
					},
					slidesPerView: 1,
					wrapperClass: 'network-slider__wrapper',

				}
			);
		},
		_bindClass(): void {
			this.closeSlider = this.closeSlider.bind(this);
			this.openSlider = this.openSlider.bind(this);
			this.handleDocumentClick = this.handleDocumentClick.bind(this);
			this.handleKeydown = this.handleKeydown.bind(this);
		},
		_createCloseButtons(): void {
			this.slider.querySelector( '.network-slider__close' )?.remove();

			this.closeButtons = Array.from(
				this.slider.querySelectorAll( '.network-slide' )
			).map( (slide: Element): HTMLButtonElement => {
				const existingButton = slide.querySelector( '.network-slide__close' ) as HTMLButtonElement|null;

				if ( existingButton ) {
					return existingButton;
				}

				const button = document.createElement( 'button' );
				button.type = 'button';
				button.className = 'network-slide__close';
				button.setAttribute( 'aria-label', 'Mitarbeiterstimme schließen' );
				slide.append( button );

				return button;
			} );
		},
		_enhanceAccessibility(): void {
			this.slider.setAttribute( 'role', 'dialog' );
			this.slider.setAttribute( 'aria-label', 'Mitarbeiterstimme' );
			this.slider.setAttribute( 'aria-hidden', 'true' );
			this.slider.setAttribute( 'tabindex', '-1' );
			this.slider.setAttribute( 'inert', '' );

			this.nodes.forEach( (node: HTMLElement): void => {
				node.setAttribute( 'aria-haspopup', 'dialog' );
				node.setAttribute( 'aria-expanded', 'false' );
			} );

			this.container.querySelector( '.network-slider__button--prev' )
				?.setAttribute( 'aria-label', 'Vorherige Mitarbeiterstimme' );
			this.container.querySelector( '.network-slider__button--next' )
				?.setAttribute( 'aria-label', 'Nächste Mitarbeiterstimme' );
		},
		_bindEvents(): void {
			this.nodes.forEach( (node: HTMLElement): void => {
				node.addEventListener( 'click', (event: Event): void => {
					event.preventDefault();
					const target = event.currentTarget as HTMLElement;
					const index = parseInt( target?.dataset?.nodeIndex ?? '1', 10 );

					this.activeNode = target;
					this.setIndex(index);
					this.openSlider();
				} );
			} );
			this.overlay.addEventListener( 'click', this.closeSlider );
			this.closeButtons.forEach( (button: HTMLButtonElement): void => {
				button.addEventListener( 'click', this.closeSlider );
			} );
			document.addEventListener( 'click', this.handleDocumentClick );
			document.addEventListener( 'keydown', this.handleKeydown );
		},
		_observeVisibility(): void {
			if ( ! ( 'IntersectionObserver' in window ) ) {
				return;
			}

			this.container.classList.add( 'network--line-pausing' );

			const observer = new IntersectionObserver(
				(entries: IntersectionObserverEntry[]): void => {
					this.container.classList.toggle(
						'network--in-view',
						entries[0]?.isIntersecting ?? false
					);
				},
				{ rootMargin: '120px 0px' }
			);

			observer.observe( this.container );
		},
		handleKeydown(event: KeyboardEvent): void {
			if ( event.key === 'Escape' && this.container.classList.contains( 'network--open' ) ) {
				this.closeSlider();
			}
		},
		handleDocumentClick(event: MouseEvent): void {
			if ( ! this.container.classList.contains( 'network--open' ) ) {
				return;
			}

			const target = event.target as Node|null;
			const activeSlide = this.slider.querySelector( '.network-slide--active' );
			const clickedControl = target instanceof Element
				&& target.closest( '.network-slider__button, .network-slide__close' );

			if (
				target
				&& ! activeSlide?.contains( target )
				&& ! clickedControl
				&& ! this.activeNode?.contains( target )
			) {
				this.closeSlider();
			}
		},
		closeSlider(): void {
			if ( ! this.container.classList.contains( 'network--open' ) ) {
				return;
			}

			this.container.classList.remove( 'network--open' );
			this.slider.setAttribute( 'aria-hidden', 'true' );
			this.slider.setAttribute( 'inert', '' );
			this.activeNode?.setAttribute( 'aria-expanded', 'false' );
			this.activeNode?.focus( { preventScroll: true } );
			this.activeNode = null;
			this.activeCloseButton = null;
		},
		openSlider(): void {
			this.container.classList.add( 'network--open' );
			this.slider.removeAttribute( 'inert' );
			this.slider.setAttribute( 'aria-hidden', 'false' );
			this.activeNode?.setAttribute( 'aria-expanded', 'true' );

			window.requestAnimationFrame( (): void => {
				this.activeCloseButton?.focus( { preventScroll: true } );
			} );
		},
		setIndex(index: number): void {
			const slideIndex = Math.max( index - 1, 0 );

			this.activeCloseButton = this.closeButtons[slideIndex] ?? null;
			this.swiper.slideTo( slideIndex, 0 );
		}
	}

	NetworkObject._init();
	return NetworkObject;
};
